



import { useState, type ChangeEvent, type ReactNode, type RefObject } from "react";
import { SignatureModal } from "../../../components/Form/SignatureModel";
import type { PLOFormTwoData } from "../../../types/PLOs/plo_two.type";


export interface AttachmentFile {
  name: string;
  [key: string]: any;
}
export interface DetailFormDocumentProps {
  formData: PLOFormTwoData;
  isEditable: boolean;
  isSigning: boolean;
  isPreview:boolean;
  sigCanvasRef: RefObject<any>;
  setIsSigning: (isSigning: boolean) => void;
  
  handleChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  // Updated to accept MouseEvent
  handleClearSignature: (e?: React.MouseEvent) => void;
  handleSaveSignature: (role?: 'headofficer' | 'USER') => void;

  // Flexible string / keyof FormData support
  renderDocumentRow: (label: string, fieldName: any) => ReactNode;
  renderDatePicker: (fieldName: any) => ReactNode;

  // Action controls
  // handleBackNavigation: () => void;
  handleClearDate?: (fieldName: keyof PLOFormTwoData) => void;
}
export const PloDetailFormTwoDocument: React.FC<DetailFormDocumentProps> = ({
  formData,
  isEditable,
  isSigning,
  isPreview = false,
  sigCanvasRef,
  setIsSigning,
  handleChange,
  handleClearSignature,
  handleSaveSignature,
  renderDocumentRow,
  renderDatePicker,
}) => {

    // Track active signature role: 'prosecutor' or 'USER' (Responsible Officer)
   const [activeSigningRole, setActiveSigningRole] = useState<'headofficer' | 'USER' | null>(null);
  
    // Check if either signature block is fully signed
    const handleOpenSignatureModal = (role: 'headofficer' | 'USER') => {
      if (!isEditable) return;
      setActiveSigningRole(role);
      setIsSigning(true);
    };

    const handleModalSave = () => {
      if (activeSigningRole) {
        handleSaveSignature(activeSigningRole);
      } else {
        handleSaveSignature();
      }
      setIsSigning(false);
      setActiveSigningRole(null);
    };
    
  return (
    <div className="lg:col-span-8 bg-white p-6 md:p-10 rounded-xl border border-slate-200 shadow-sm select-text print:border-none print:shadow-none print:p-0">
      <div className="flex justify-between items-center mb-6">
        {/* <span className="text-xs text-slate-400">ID: {formData.id || 'N/A'}</span> */}
        <span className="text-xs font-semibold text-slate-600 tracking-wider">PTI ပုံစံ</span>
      </div>

      <div className="text-center mb-8">
        <h2 className="text-xl font-bold text-slate-900 leading-relaxed">
          အကြိုတွေ့ဆုံမေးမြန်းခြင်း ပြီးပြတ်ချက်ပုံစံ
        </h2>
      </div>

      <form className="space-y-4">
                        
                        {/* Main Block Section 1-5 */}
                        {renderDocumentRow('၁။ တရားရုံးအမည်', 'courtName')}
                        {renderDocumentRow('၂။ အမှုအမှတ်', 'caseNumber')}
                        {renderDocumentRow('၃။ တရားလိုနှင့်တရားခံအမည်', 'plaintiffOrAccused')}
                        {renderDocumentRow('၄။ တွေ့ဆုံမေးမြန်းသူ (အမှုလိုက်ဥပဒေအရာရှိ)', 'interviewerDetails')}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 py-2">
                          <label className="text-slate-800 font-medium text-sm sm:w-80 shrink-0">
                            ၅။ တရားရုံးစွဲဆိုသည့်ရက်စွဲ
                          </label>
                          <div className="flex-grow max-w-xs">{renderDatePicker('courtSubmissionDate')}</div>
                        </div>
      
                        {/* (က) အခြေခံအမှုသတင်းအချက်အလက် */}
                        <div className="pt-4">
                          <h3 className="text-sm font-bold text-slate-900 mb-2">(က) အခြေခံအမှုသတင်းအချက်အလက်</h3>
                          <div className="pl-4 space-y-1">
                            {renderDocumentRow('• စွဲဆိုသည့်ဥပဒေ ပုဒ်မ', 'chargeSection')}
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 py-2">
                              <label className="text-slate-800 font-medium text-sm sm:w-80 shrink-0">
                                • အမှုဖြစ်ပွားသည့်ရက်စွဲ
                              </label>
                              <div className="flex-grow max-w-xs">{renderDatePicker('incidentDate')}</div>
                            </div>
                            {renderDocumentRow('• အမှုဖြစ်ပွားသည့်နေရာ', 'incidentLocation')}
                            {renderDocumentRow('• အမှုစစ်ရဲအရာရှိ', 'investigatorInfo')}
                          </div>
                        </div>
      
                        {/* (ခ) အကြံတွေ့ဆုံမေးမြန်းခြင်း သတင်းအချက်အလက် */}
                        <div className="pt-4">
                          <h3 className="text-sm font-bold text-slate-900 mb-2">(ခ) အကြံတွေ့ဆုံမေးမြန်းခြင်း သတင်းအချက်အလက်</h3>
                          <div className="pl-4 space-y-1">
                            
                            <div className="flex flex-col gap-1 py-1.5">
                              <label className="text-slate-800 font-medium text-sm">
                                • အကြံတွေ့ဆုံမေးမြန်းမှုပြုလုပ်ရသည့်အကြောင်းပြချက် -
                              </label>
                              <p className="text-xs text-slate-400 italic font-normal -mt-1">(ဤအမှုတွင် အကြံတွေ့ဆုံမေးမြန်းမှုပြုလုပ်ခြင်း သို့မဟုတ် မပြုလုပ်ခြင်း အကြောင်းပြချက်ကို ဖော်ပြပါ)</p>
                              <div className="relative w-full mt-1">
                                {isPreview ? (
                                  <p className="text-sm text-slate-900 font-medium whitespace-pre-wrap bg-slate-50/50 p-2.5 rounded min-h-[3rem]">
                                    {formData.interviewReason || <span className="text-slate-300 italic font-normal">(ကွက်လပ်)</span>}
                                  </p>
                                ) : (
                                  <input type="text" name="interviewReason" value={formData.interviewReason} onChange={handleChange} placeholder="........................................................................................................................................................................" className="w-full bg-transparent border-none p-0 text-sm focus:outline-none focus:ring-0" />
                                )}
                              </div>
                            </div>
      
                            {renderDocumentRow('• တွေ့ဆုံမေးမြန်းခံရသူ', 'intervieweeName')}
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 py-2">
                              <label className="text-slate-800 font-medium text-sm sm:w-80 shrink-0">
                                • အကြံတွေ့ဆုံမေးမြန်းမှုပြုလုပ်သည့်ရက်စွဲ
                              </label>
                              <div className="flex-grow max-w-xs">{renderDatePicker('interviewDate')}</div>
                            </div>
                            {renderDocumentRow('• အကြံတွေ့ဆုံမေးမြန်းမှုပြုလုပ်သည့်နေရာ', 'interviewLocation')}
                            
                            <div className="flex flex-col gap-1 py-1.5">
                              <label className="text-slate-800 font-medium text-sm">• အကူအညီပေးသည့် အဖွဲ့အစည်း</label>
                              <p className="text-xs text-slate-400 italic font-normal -mt-1">(အကြံတွေ့ဆုံမေးမြန်းမှုပြုလုပ်ရန် စီစဉ်ထောက်ပံ့ပေးသည့်/ကူညီပေးသည့် အဖွဲ့အစည်းကို ဖော်ပြပါ)</p>
                              <div className="relative w-full mt-1">
                                {isPreview ? (
                                  <span className="text-sm text-slate-900 font-medium block min-h-[1.5rem] bg-slate-50/50 rounded px-1">{formData.supportingOrganization || ' '}</span>
                                ) : (
                                  <input type="text" name="supportingOrganization" value={formData.supportingOrganization} onChange={handleChange} placeholder="........................................................................................................................................................................" className="w-full bg-transparent border-none p-0 text-sm focus:outline-none focus:ring-0" />
                                )}
                              </div>
                            </div>
      
                            {renderDocumentRow('• တွေ့ဆုံမေးမြန်းမှုတွင် တက်ရောက်သည့် လေ့လာသူ', 'observerName')}
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 py-2">
                            <label className="text-slate-800 font-medium text-sm sm:w-80 shrink-0">
                              • တွေ့ဆုံမေးမြန်းခံရသူ တရားရုံး သက်သေခံသည့်ရက်စွဲ
                            </label>
                            <div className="flex-grow max-w-xs">{renderDatePicker('courtSummonsDate')}</div>
                          </div>
                          </div>
                        </div>
      
                        {/* Bottom Structural Section Fields 6 and 7 */}
                        <div className="pt-4 space-y-1">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 py-2">
                          <label className="text-slate-800 font-medium text-sm sm:w-80 shrink-0">
                            ၆။ အပြီးသတ်အမိန့်ချမှတ်သည့်ရက်စွဲ
                          </label>
                          <div className="flex-grow max-w-xs">{renderDatePicker('finalOrderDate')}</div>
                        </div>
                          {renderDocumentRow('၇။ စစ်ဆေးစီရင်သည့်တရားသူကြီး', 'examiningJudge')}
                        </div>
      
                        {/* ၈။ အပြီးသတ်အမိန့် Textarea Field Row */}
                        <div className="flex flex-col gap-1 py-2">
                          <label className="text-slate-800 font-medium text-sm">၈။ အပြီးသတ်အမိန့်</label>
                          <div className="relative w-full">
                            {isPreview ? (
                              <p className="text-sm text-slate-900 font-medium leading-relaxed whitespace-pre-wrap bg-slate-50/50 p-3 rounded min-h-[5rem]">
                                {formData.finalOrderSummary || <span className="text-slate-300 italic font-normal">(ကွက်လပ်)</span>}
                              </p>
                            ) : (
                              <textarea
                                name="finalOrderSummary"
                                rows={3}
                                value={formData.finalOrderSummary}
                                onChange={handleChange}
                                placeholder=""
                                className="w-full bg-transparent border-none p-2 text-sm text-slate-900 placeholder-slate-300 focus:outline-none focus:ring-0 font-medium leading-relaxed resize-none"
                                style={{ backgroundImage: 'linear-gradient(transparent, transparent 27px, #cbd5e1 27px, #cbd5e1 28px)', backgroundSize: '100% 28px', lineHeight: '28px' }}
                              />
                            )}
                          </div>
                        </div>
      
                        {/* ၉။ အမှုလိုက်ဥပဒေအရာရှိ သဘောထားမှတ်ချက် (Signature Set 1) */}
                        <div className="pt-6 border-t border-slate-100">
                          <div className="flex flex-col gap-1 py-1">
                            <label className="text-slate-900 font-bold text-sm">၉။ အမှုလိုက်ဥပဒေအရာရှိ၏ သဘောထားမှတ်ချက်</label>
                            <div className="relative w-full mt-1">
                              {isPreview ? (
                                <p className="text-sm text-slate-800 bg-slate-50/50 p-2 rounded min-h-[3rem] whitespace-pre-wrap">{formData.interviewerRemarks || ' '}</p>
                              ) : (
                                <input type="text" name="interviewerRemarks" value={formData.interviewerRemarks} onChange={handleChange} placeholder="........................................................................................................................................................................" className="w-full bg-transparent border-none p-0 text-sm focus:outline-none focus:ring-0" />
                              )}
                            </div>
                          </div>
      
                          {/* Signature Block 1 */}
                          <div className="flex flex-col items-end gap-1.5 text-sm text-slate-800 pr-4 mt-4">
                            <div className="w-72 space-y-2">
                              <div className="flex items-center">
                                <span className="w-24 text-right text-sm whitespace-nowrap mr-2">လက်မှတ်</span>
                                {isPreview ? (
                                  <div className="flex-1 min-h-[32px] flex items-center justify-center border-b border-dashed border-slate-300">
                                    {formData.interviewerSignImage ? <img src={formData.interviewerSignImage} alt="Signature" className="h-8 object-contain mix-blend-multiply" /> : <span className="text-slate-300 italic text-xs">(ကွက်လပ်)</span>}
                                  </div>
                                ) : (
                                  <div onClick={() => handleOpenSignatureModal('USER')} className="flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center cursor-pointer hover:bg-slate-50 relative group">
                                    {formData.interviewerSignImage ? (
                                      <>
                                        <img src={formData.interviewerSignImage} alt="Signature" className="h-8 object-contain mix-blend-multiply" />
                                        <span className="absolute inset-0 bg-black/40 text-white text-[10px] opacity-0 group-hover:opacity-100 flex items-center justify-center transition rounded">လက်မှတ်အသစ်ပြင်ရန်</span>
                                      </>
                                    ) : (
                                      <span className="text-gray-400 text-xs">✍️ လက်မှတ်ထိုးရန်</span>
                                    )}
                                    {formData.interviewerSignImage && (
                                    <button
                                      type="button"
                                      onClick={handleClearSignature}
                                      title="လက်မှတ်ရှင်းလင်းမည်"
                                      className="absolute -right-6 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden"
                                    >
                                      ✕
                                    </button>
                                  )}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center">
                                <span className="w-24 text-right text-sm whitespace-nowrap mr-2">အမည်</span>
                                {isPreview ? <span className="flex-1 text-center font-medium border-b border-dashed border-slate-300 min-h-[24px]">{formData.interviewerSignName || '-'}</span> : <input type="text" name="interviewerSignName" value={formData.interviewerSignName} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center p-0 text-sm" />}
                              </div>
                              <div className="flex items-center">
                                <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">ရက်စွဲ</span>
                                {isPreview ? <span className="flex-1 text-center font-medium border-b border-dashed border-slate-300 min-h-[24px]">{formData.interviewerSignDate || '-'}</span> : <div className="flex-grow">{renderDatePicker('interviewerSignDate')}</div>}
                              </div>
                            </div>
                          </div>
                        </div>
      
                        {/* ၁၀။ တာဝန်ခံဥပဒေအရာရှိ သဘောထားမှတ်ချက် (Signature Set 2) */}
                        <div className="pt-6 border-t border-slate-100">
                          <div className="flex flex-col gap-1 py-1">
                            <label className="text-slate-900 font-bold text-sm">၁၀။ တာဝန်ခံဥပဒေအရာရှိ၏ သဘောထားမှတ်ချက်</label>
                            <div className="relative w-full mt-1">
                              {isPreview ? (
                                <p className="text-sm text-slate-800 bg-slate-50/50 p-2 rounded min-h-[3rem] whitespace-pre-wrap">{formData.headOfficerRemarks || ' '}</p>
                              ) : (
                                <input type="text" name="headOfficerRemarks" value={formData.headOfficerRemarks} onChange={handleChange} placeholder="........................................................................................................................................................................" className="w-full bg-transparent border-none p-0 text-sm focus:outline-none focus:ring-0" />
                              )}
                            </div>
                          </div>
      
                          {/* Signature Block 2 */}
                          <div className="flex flex-col items-end gap-1.5 text-sm text-slate-800 pr-4 mt-4">
                            <div className="w-72 space-y-2">
                              <div className="flex items-center">
                                <span className="w-24 text-right text-sm whitespace-nowrap mr-2">လက်မှတ်</span>
                                {isPreview ? (
                                  <div className="flex-1 min-h-[32px] flex items-center justify-center border-b border-dashed border-slate-300">
                                    {formData.headOfficerSignImage ? <img src={formData.headOfficerSignImage} alt="Signature" className="h-8 object-contain mix-blend-multiply" /> : <span className="text-slate-300 italic text-xs">(ကွက်လပ်)</span>}
                                  </div>
                                ) : (
                                  <div onClick={() => handleOpenSignatureModal('headofficer')} className="flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center cursor-pointer hover:bg-slate-50 relative group">
                                    {formData.headOfficerSignImage ? (
                                      <>
                                        <img src={formData.headOfficerSignImage} alt="Signature" className="h-8 object-contain mix-blend-multiply" />
                                        <span className="absolute inset-0 bg-black/40 text-white text-[10px] opacity-0 group-hover:opacity-100 flex items-center justify-center transition rounded">လက်မှတ်အသစ်ပြင်ရန်</span>
                                      </>
                                    ) : (
                                      <span className="text-gray-400 text-xs">✍️ လက်မှတ်ထိုးရန်</span>
                                    )}
                                    {formData.headOfficerSignImage && (
                                    <button
                                      type="button"
                                      onClick={handleClearSignature}
                                      title="လက်မှတ်ရှင်းလင်းမည်"
                                      className="absolute -right-6 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden"
                                    >
                                      ✕
                                    </button>
                                  )}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center">
                                <span className="w-24 text-right text-sm whitespace-nowrap mr-2">အမည်</span>
                                {isPreview ? <span className="flex-1 text-center font-medium border-b border-dashed border-slate-300 min-h-[24px]">{formData.headOfficerSignName || '-'}</span> : <input type="text" name="headOfficerSignName" value={formData.headOfficerSignName} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center p-0 text-sm" />}
                              </div>
                              <div className="flex items-center">
                                <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">ရက်စွဲ</span>
                                {isPreview ? <span className="flex-1 text-center font-medium border-b border-dashed border-slate-300 min-h-[24px]">{formData.headOfficerSignDate || '-'}</span> : <div className="flex-grow">{renderDatePicker('headOfficerSignDate')}</div>}
                              </div>
                            </div>
                          </div>
                        </div>
      
                        {/* Footer Informative Note Disclaimer Description Text Block */}
                        <div className="pt-8 text-xs text-slate-500 leading-relaxed border-t border-slate-100 space-y-1">
                          <p className="font-bold text-slate-700">မှတ်ချက်။</p>
                          <p>ဤအမှုလိုက်စာတွဲအား အမှုလိုက်ဥပဒေအရာရှိက ပြည့်စုံစွာ ဖြည့်စွက်ပြီး အမှုအား အပြီးသတ် အမိန့်ချမှတ်ပြီးနောက် ( ၃ ) ရက်အတွင်း တာဝန်ခံဥပဒေအရာရှိထံ တင်ပြရမည်။ တာဝန်ခံဥပဒေအရာရှိက ဤအမှုလိုက် စာတွဲအား လက်ခံရရှိပြီး ( ၇ ) ရက်အတွင်း သက်ဆိုင်ရာ ပြည်နယ် သို့မဟုတ် တိုင်းဒေသကြီး ဥပဒေအရာရှိထံ တင်ပြရမည်။</p>
                        </div>    
                      </form>
      
      {/* Signature Canvas Dialog */}
      {isSigning && isEditable && (
        <SignatureModal
          isOpen={isSigning}
          sigCanvasRef={sigCanvasRef}
          onClose={() => {
            setIsSigning(false);
            setActiveSigningRole(null);
          }}
          onClear={() => sigCanvasRef.current?.clear()}
          onSave={handleModalSave}
        />
        )}
    </div>
  );
};